import { FunctionComponent, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as yup from "yup";
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_RESOURCE_OVERVIEW, GET_ALL_SKILLS } from '../../api/query';
import { CREATE_GOAL } from '../../api/mutation';
import { useNavigate } from 'react-router-dom';
import { showError, showSuccess } from '../../components/common/ToastNotification';
import Breadcrumb from '../../components/common/Breadcrumb';
import DateSelector from '../../components/common/DateSelector';
import InputsDropdownMultipleSearchSelect from '../../components/common/InputsDropdownMultipleSearchSelect';
import InputsNew from '../../components/common/InputsNew';
import MemberAvailabilityDropdown from '../../components/common/MemberAvailabilityDropdown';
import backArrow from "../../assets/task/back_arrow.svg";
import arrowIconaccordion from "../../assets/task/up-arrow.png";
import downarrowIconaccordion from "../../assets/task/down-arrow.png";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";
import { addDays, addHours, addMinutes, differenceInDays, format } from 'date-fns';

// Phase definitions with tasks and checklists
const CAREBRAVO_PHASES = [
  {
    id: 'phase1',
    name: 'PHASE 1: CS Confirmation (Day 0)',
    taskTypeID: '1', // Will be replaced with actual API ID
    checklists: [
      { id: '1', name: 'Contact customer within 24 hours' },
      { id: '2', name: 'Confirm Go-Live date' },
      { id: '3', name: 'Send confirmation email with attachments' },
      { id: '4', name: 'Notify Data Team to begin' },
      { id: '5', name: 'Notify QA Team to prepare' }
    ]
  },
  {
    id: 'phase2',
    name: 'PHASE 2: Data Export (Days 1-2)',
    taskTypeID: '2',
    checklists: [
      { id: '6', name: 'Log into Caryfy with customer credentials' },
      { id: '7', name: 'Export all customer data (clients, caregivers, schedules, visits, billing, documents)' },
      { id: '8', name: 'Run data integrity check' },
      { id: '9', name: 'Notify CS and QA of completion' }
    ]
  },
  {
    id: 'phase3',
    name: 'PHASE 3: Data Import (Days 3-4)',
    taskTypeID: '3',
    checklists: [
      { id: '10', name: 'Map Caryfy fields to CareBravo schema' },
      { id: '11', name: 'Import data to staging environment' },
      { id: '12', name: 'Run automated validation script' },
      { id: '13', name: 'Manual spot-check (10 clients, 10 caregivers, 20 visits)' },
      { id: '14', name: 'Fix any critical data issues' },
      { id: '15', name: 'Notify QA: Ready for comprehensive audit' }
    ]
  },
  {
    id: 'phase4',
    name: 'PHASE 4: QA Audit (Day 6)',
    taskTypeID: '4',
    checklists: [
      { id: '16', name: 'Comprehensive QA audit (data validation, workflow tests, browser/device tests)' },
      { id: '17', name: 'CREATE FIRST SHIFT MANUALLY (critical)' },
      { id: '18', name: 'Generate QA Audit Report' },
      { id: '19', name: 'If first shift successful, notify Data Team to export schedules' },
      { id: '20', name: 'If critical bugs found, notify Engineering' }
    ]
  },
  {
    id: 'phase5',
    name: 'PHASE 5: Schedule Export (Day 6 PM)',
    taskTypeID: '5',
    checklists: [
      { id: '21', name: 'WAIT for QA first shift confirmation' },
      { id: '22', name: 'Export all future schedules from Caryfy (Day 8 onward)' },
      { id: '23', name: 'Import schedules to CareBravo staging' },
      { id: '24', name: 'Run schedule validation' },
      { id: '25', name: 'Notify QA: Schedules ready for validation' }
    ]
  },
  {
    id: 'phase6',
    name: 'PHASE 6: Bug Fixes & Schedule Validation (Day 7 AM)',
    taskTypeID: '6',
    checklists: [
      { id: '26', name: 'QA re-tests critical bugs' },
      { id: '27', name: 'QA validates schedules' },
      { id: '28', name: 'Engineering fixes any remaining critical bugs' },
      { id: '29', name: 'QA approves for production OR escalates if not ready' }
    ]
  },
  {
    id: 'phase7',
    name: 'PHASE 7: Production Migration (Day 7 PM)',
    taskTypeID: '7',
    checklists: [
      { id: '30', name: 'Move data from staging to production' },
      { id: '31', name: 'Run final smoke test' },
      { id: '32', name: 'Generate Production Ready Report' },
      { id: '33', name: 'Notify CS: Ready for Go-Live tomorrow' }
    ]
  },
  {
    id: 'phase8',
    name: 'PHASE 8: Go-Live (Day 8)',
    taskTypeID: '8',
    checklists: [
      { id: '34', name: 'CS conducts Go-Live call (45-60 min)' },
      { id: '35', name: 'Data Team on standby for issues' },
      { id: '36', name: 'QA Team on standby for issues' },
      { id: '37', name: 'Send post-call recap email' },
      { id: '38', name: 'Send Go-Live call recording to customer' },
      { id: '39', name: 'Update WizKind: Go-Live complete' }
    ]
  },
  {
    id: 'phase9',
    name: 'PHASE 9: Post-Launch Support (Days 9-14)',
    taskTypeID: '9',
    checklists: [
      { id: '40', name: 'Day 9 - Send "How was your first day?" email' },
      { id: '41', name: 'Day 10 - Check-in call (15 min)' },
      { id: '42', name: 'Days 9-14 - Daily health monitoring by QA' },
      { id: '43', name: 'Days 11-13 - Active CS monitoring (4-hour response time)' },
      { id: '44', name: 'Day 13 - Automated Caryfy cutoff reminder sent' },
      { id: '45', name: 'Day 14 - Send final check-in email + satisfaction survey' }
    ]
  },
  {
    id: 'phase10',
    name: 'PHASE 10: Caryfy Cutoff & Completion (Day 14-15)',
    taskTypeID: '10',
    checklists: [
      { id: '46', name: 'Day 14 EOD - Execute Caryfy cutoff script' },
      { id: '47', name: 'Verify read-only access works' },
      { id: '48', name: 'Verify CareBravo archive link works' },
      { id: '49', name: 'Mark WizKind Goal as Complete' },
      { id: '50', name: 'Generate lessons learned (if any issues)' }
    ]
  }
];

interface PhaseTask {
  phaseId: string;
  phaseName: string;
  taskTypeID: string;
  taskName: string;
  assignedMemberID: string;
  assignedMember: string;
  taskSkillID: string;
  taskSkill: string;
  startTaskDate: string | null;
  durationDays: number;
  durationTimeHour: number;
  durationTimeMinute: number;
  endDate: string | null;
  checklists: { id: string; name: string }[];
}

const CarebravoTransitionTemplate: FunctionComponent = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const employeeID = userInfo?.employee_id || '';
  const organizationID = userInfo?.agencies?.id || '';

  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
  const [assignedMemberOptions, setAssignedMemberOptions] = useState<{ key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[]>([]);
  const [skillOptions, setSkillOptions] = useState<{ key: string; name: string }[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<any[]>([]);
  const [expandedPhases, setExpandedPhases] = useState<{ [key: string]: boolean }>({});
  const [phaseTasks, setPhaseTasks] = useState<PhaseTask[]>([]);
  const [clearMemberSelected, setClearMemberSelected] = useState(false);

  const addTaskSchema = yup.object().shape({
    startGoalDate: yup.string().required("This field is required"),
    endGoalDate: yup.string().required("This field is required"),
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
    watch,
    control,
  } = useForm<any>({
    resolver: yupResolver(addTaskSchema),
    defaultValues: {
      projectName: "carebravo",
      projectID: "1", // You'll need to set the actual project ID
      goalFor: "Customer Success",
      goalType: "CareBravo Onboarding",
      goalID: "1", // You'll need to set the actual goal type ID
    }
  });

  // API calls
  const { data: skillData } = useQuery(GET_ALL_SKILLS, {
    fetchPolicy: 'no-cache',
  });

  const [createGoal, { loading: createGoalLoading }] = useMutation(CREATE_GOAL, {
    fetchPolicy: 'no-cache',
  });

  const [
    getResourceOverview,
    { data: resourceOverviewData, loading: resourceOverviewLoading },
  ] = useLazyQuery(GET_RESOURCE_OVERVIEW, {
    fetchPolicy: 'no-cache'
  });

  const watchStartGoalDate: string = watch('startGoalDate');
  const watchEndGoalDate: string = watch('endGoalDate');

  // Skills useEffect
  useEffect(() => {
    if (skillData && skillData.get_all_skills?.skills?.length > 0) {
      const options = skillData.get_all_skills?.skills?.map((skill: any) => ({
        key: skill.id,
        name: skill?.skill_name
      }));
      setSkillOptions(options);
    }
  }, [skillData]);

  // Initialize phase tasks when component mounts
  useEffect(() => {
    const initialTasks: PhaseTask[] = CAREBRAVO_PHASES.map(phase => ({
      phaseId: phase.id,
      phaseName: phase.name,
      taskTypeID: phase.taskTypeID,
      taskName: '',
      assignedMemberID: '',
      assignedMember: '',
      taskSkillID: '',
      taskSkill: '',
      startTaskDate: null,
      durationDays: 1,
      durationTimeHour: 0,
      durationTimeMinute: 0,
      endDate: null,
      checklists: phase.checklists
    }));
    setPhaseTasks(initialTasks);

    // Expand all phases by default
    const expanded: { [key: string]: boolean } = {};
    CAREBRAVO_PHASES.forEach(phase => {
      expanded[phase.id] = true;
    });
    setExpandedPhases(expanded);
  }, []);

  // Fetch resource overview when dates change
  useEffect(() => {
    if (watchStartGoalDate && watchEndGoalDate && organizationID) {
      // Clear members when dates change
      setSelectedMembers([]);
      setPhaseTasks(prev => prev.map(task => ({
        ...task,
        assignedMemberID: '',
        assignedMember: '',
        startTaskDate: null,
        endDate: null
      })));
      setClearMemberSelected(true);

      const input = {
        agency_id: parseInt(organizationID),
        start_date: watchStartGoalDate,
        end_date: watchEndGoalDate,
        project_id: 1 // CareBravo project ID - update with actual ID
      };

      getResourceOverview({
        variables: {
          input_data: input
        }
      });

      setTimeout(() => setClearMemberSelected(false), 100);
    }
  }, [watchStartGoalDate, watchEndGoalDate, organizationID]);

  // Process resource overview data
  useEffect(() => {
    if (resourceOverviewData?.get_resource_overview?.status_code === 200) {
      const resourceData = resourceOverviewData.get_resource_overview.data;

      if (resourceData?.members && resourceData.members.length > 0) {
        // Sort members: available first, then non-available
        const sortedMembers = [...resourceData.members].sort((a, b) => {
          if (a.is_available && !b.is_available) return -1;
          if (!a.is_available && b.is_available) return 1;
          return 0;
        });

        const memberOptions = sortedMembers.map((member: any) => ({
          key: member.employee_id,
          name: `${member.first_name} ${member.last_name}`,
          isAvailable: member.is_available,
          availableHours: Math.max(0, member.total_working_hours - member.allocated_working_hours),
          totalHours: member.total_working_hours,
          utilizationPercentage: member.utilization_percentage
        }));

        setAssignedMemberOptions(memberOptions);
      } else {
        setAssignedMemberOptions([]);
      }
    }
  }, [resourceOverviewData]);

  const handleSelectMembers = (key: any, name: any, type: any) => {
    const allOptions = assignedMemberOptions;

    if (key === "all") {
      if (name === true) {
        // Deselect all
        setSelectedMembers([]);
      } else {
        // Select all
        setSelectedMembers(allOptions);
      }
    } else {
      setSelectedMembers((prev) => {
        const exists = prev.some((item) => item.key == key);
        if (exists) {
          return prev.filter((item) => item.key != key);
        } else {
          return [...prev, { key, name, type }];
        }
      });
    }
  };

  const handleRemoveMember = (key: string) => {
    setSelectedMembers((prev) => prev.filter((item) => item.key !== key));
  };

  // Skills handler functions
  const handleSelectSkills = (key: any, name: any, type: any) => {
    const allOptions = skillOptions;

    if (key === "all") {
      if (name === true) {
        // Deselect all
        setSelectedSkills([]);
      } else {
        // Select all
        setSelectedSkills(allOptions);
      }
    } else {
      setSelectedSkills((prev) => {
        const exists = prev.some((item) => item.key == key);
        if (exists) {
          // REMOVE if already selected
          return prev.filter((item) => item.key != key);
        } else {
          // ADD if not selected
          return [...prev, { key, name, type }];
        }
      });
    }
  };

  const handleRemoveSkill = (key: string) => {
    // Check if this skill is used in any task
    const isSkillUsedInTasks = phaseTasks.some(task => task.taskSkillID === key);

    if (isSkillUsedInTasks) {
      showError('This skill is being used in one or more tasks. Please remove it from tasks first.');
      return;
    }

    setSelectedSkills((prev) => prev.filter((item) => item.key !== key));
  };

  const togglePhaseAccordion = (phaseId: string) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  const updatePhaseTask = (phaseId: string, field: keyof PhaseTask, value: any) => {
    setPhaseTasks(prev => prev.map(task => {
      if (task.phaseId === phaseId) {
        const updatedTask = { ...task, [field]: value };

        // Auto-calculate end date when start date or duration changes
        if (field === 'startTaskDate' || field === 'durationDays' || field === 'durationTimeHour' || field === 'durationTimeMinute') {
          const startDate = field === 'startTaskDate' ? value : task.startTaskDate;
          const days = field === 'durationDays' ? value : task.durationDays;
          const hours = field === 'durationTimeHour' ? value : task.durationTimeHour;
          const minutes = field === 'durationTimeMinute' ? value : task.durationTimeMinute;

          if (startDate && (days || hours || minutes)) {
            updatedTask.endDate = calculateEndDate(startDate, days, hours, minutes);
          }
        }

        return updatedTask;
      }
      return task;
    }));
  };

  const handleSelectMember = (phaseId: string, key: any, name: any) => {
    updatePhaseTask(phaseId, 'assignedMemberID', key);
    updatePhaseTask(phaseId, 'assignedMember', name);
  };

  const handleSelectTaskSkill = (phaseId: string, key: any, name: any) => {
    updatePhaseTask(phaseId, 'taskSkillID', key.toString());
    updatePhaseTask(phaseId, 'taskSkill', name);
  };

  const calculateEndDate = (startDate: string, durationDays: number, durationHours: number, durationMinutes: number) => {
    if (!startDate || (!durationDays && !durationHours && !durationMinutes)) {
      return null;
    }

    const startDateObj = new Date(startDate);
    startDateObj.setHours(0, 0, 0, 0);

    let endDateObj = addMinutes(startDateObj, durationMinutes);
    endDateObj = addHours(endDateObj, durationHours);
    endDateObj = addDays(endDateObj, (durationDays - 1));

    return format(endDateObj, "MM-dd-yyyy");
  };

  const validatePhaseTasks = (): boolean => {
    if (selectedMembers.length === 0) {
      showError('Please select at least one Goal member');
      return false;
    }

    if (selectedSkills.length === 0) {
      showError('Please select at least one Goal skill');
      return false;
    }

    for (const task of phaseTasks) {
      if (!task.taskName || task.taskName.trim() === '') {
        showError(`Please enter task name for ${task.phaseName}`);
        return false;
      }

      if (!task.assignedMemberID) {
        showError(`Please select assignee for ${task.phaseName}`);
        return false;
      }

      if (!task.taskSkillID) {
        showError(`Please select skill for ${task.phaseName}`);
        return false;
      }

      if (!task.startTaskDate) {
        showError(`Please select start date for ${task.phaseName}`);
        return false;
      }

      const totalMinutes = (task.durationTimeHour * 60) + task.durationTimeMinute;
      if (totalMinutes < 15) {
        showError(`Duration must be at least 15 minutes for ${task.phaseName}`);
        return false;
      }
    }
    return true;
  };

  async function onSubmit(data: any): Promise<void> {
    if (!validatePhaseTasks()) {
      return;
    }

    // Prepare tasks array for API
    const allTasks = phaseTasks.map(phaseTask => {
      const checklists = phaseTask.checklists.map((checklist) => ({
        task_id: parseInt(phaseTask.taskTypeID),
        checklist_id: parseInt(checklist.id),
        status: false,
        created_by: parseInt(employeeID),
      }));

      // Parse end date
      let ed = null;
      if (phaseTask.endDate) {
        const [month, day, year] = phaseTask.endDate.split('-');
        ed = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      return {
        is_new_task: true,
        goal_id: 1,
        static_goal_id: parseInt(data.goalID),
        static_task_id: parseInt(phaseTask.taskTypeID),
        task_title: phaseTask.taskName,
        task_start_date: phaseTask.startTaskDate ? `${phaseTask.startTaskDate}T00:00:00.000Z` : null,
        task_end_date: ed ? `${ed}T23:59:59.000Z` : null,
        dependency_task: null,
        created_by: parseInt(employeeID),
        days: phaseTask.durationDays,
        hours: phaseTask.durationTimeHour,
        mins: phaseTask.durationTimeMinute,
        checklists: checklists,
        assigned_to: parseInt(phaseTask.assignedMemberID),
        skill_ids: phaseTask.taskSkillID ? [parseInt(phaseTask.taskSkillID)] : [],
      };
    });

    const goalMembers = selectedMembers.map(m => m.key);
    const skillIds = selectedSkills.map(s => parseInt(s.key));

    const goalData = {
      goal_title: `CareBravo Onboarding - ${data.startGoalDate}`,
      project_id: parseInt(data.projectID),
      static_goal_id: parseInt(data.goalID),
      is_new_goal: true,
      goal_start_date: `${data.startGoalDate}T00:00:00.000Z`,
      goal_end_date: `${data.endGoalDate}T23:59:59.000Z`,
      created_by: parseInt(employeeID),
      existing_goal_id: null,
      description: 'CareBravo Transition Template',
      goal_members: goalMembers,
      removed_member_ids: [],
      idea_box_goal_id: null,
      tasks: allTasks,
      attachment_list: [],
      links: [],
      skill_ids: skillIds
    };

    try {
      const response = await createGoal({ variables: { goal_data: goalData } });
      if (response.data.create_goal.status_code === 200) {
        showSuccess(response.data.create_goal.message);
        navigate('/dashboard');
      }
      if (response.data.create_goal.status_code === 400) {
        showError(response.data.create_goal.message);
      }
    } catch (error: any) {
      showError(error.message);
    }
  }

  function onError(errors: any) {
    const firstError = Object.keys(errors)[0];
    if (firstError && errors[firstError]?.message) {
      showError(errors[firstError].message);
    }
  }

  const handleKeyDown = (event: any) => {
    const { key } = event;
    if (
      key === "Backspace" ||
      key === "Delete" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Tab" ||
      key === "Enter"
    ) {
      return;
    }
    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = event.clipboardData.getData("text");
    if (!/^\d+$/.test(paste)) {
      event.preventDefault();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} id="cts-respon-p-dv" className="w-full relative overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope">
      <div className="flex-1 flex flex-col items-start justify-start gap-6">
        <Breadcrumb />
        <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
          <img 
            className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" 
            alt="" 
            src={backArrow}
            onClick={() => navigate(-1)}
          />
          <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
            CareBravo Transition Template
          </h3>
        </div>

        {/* Goal Details Section */}
        <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full">
          <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">Goal Details</h5>
          
          <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
            {/* Project Name - Disabled */}
            <InputsNew
              label="Project Name*"
              contentPlaceholder="carebravo"
              type="text"
              value="carebravo"
              disabled={true}
            />

            {/* Goal For - Disabled */}
            <InputsNew
              label="Goal For*"
              contentPlaceholder="Customer Success"
              type="text"
              value="Customer Success"
              disabled={true}
            />

            {/* Goal Type - Disabled */}
            <InputsNew
              label="Goal Type*"
              contentPlaceholder="CareBravo Onboarding"
              type="text"
              value="CareBravo Onboarding"
              disabled={true}
            />

            {/* Start Date */}
            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
              <Controller
                name="startGoalDate"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="Start Date*"
                    selectedDate={field.value}
                    onChange={(date: string | null) => {
                      const endDate = watch('endGoalDate');

                      if (endDate && date) {
                        const end = new Date(endDate);
                        const start = new Date(date);

                        if (start > end) {
                          showError('End date cannot be earlier than start date.');
                          trigger('endGoalDate');
                          return;
                        }

                        const diffDays = differenceInDays(end, start);
                        if (diffDays > 15) {
                          showError('End date cannot be more than 15 days after the start date.');
                          trigger('endGoalDate');
                          return;
                        }
                      }

                      field.onChange(date);
                      setValue('startGoalDate', date);
                      trigger('startGoalDate');
                    }}
                    error={errors.startGoalDate?.message}
                    nothideParsedValue={true}
                  />
                )}
              />
            </div>

            {/* End Date */}
            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
              <Controller
                name="endGoalDate"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="End Date*"
                    selectedDate={field.value}
                    onChange={(date: string | null) => {
                      const startDate = watch('startGoalDate');

                      if (startDate && date) {
                        const start = new Date(startDate);
                        const end = new Date(date);

                        if (end < start) {
                          showError('End date cannot be earlier than start date.');
                          trigger('endGoalDate');
                          return;
                        }

                        const diffDays = differenceInDays(end, start);
                        if (diffDays > 15) {
                          showError('End date cannot be more than 15 days after the start date.');
                          trigger('endGoalDate');
                          return;
                        }
                      }

                      field.onChange(date);
                      setValue('endGoalDate', date);
                      trigger('endGoalDate');
                    }}
                    error={errors.endGoalDate?.message}
                    nothideParsedValue={true}
                  />
                )}
              />
            </div>
          </div>

          {/* Goal Members Section */}
          <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
            <div className="items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg addtsk_goal-dv">
              {resourceOverviewLoading ? (
                <div className="flex flex-col gap-2">
                  <label className="font-semibold pb-2 dark:text-white">Goal Members*</label>
                  <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Loading member availability...</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="memberavailability-input_main">
                  <MemberAvailabilityDropdown
                    label="Goal Members*"
                    placeHolder={assignedMemberOptions.length === 0 ? "Please select goal dates first" : "Choose"}
                    options={assignedMemberOptions}
                    onSelect={handleSelectMembers}
                    inputType="checkbox"
                    checkedItems={selectedMembers}
                    clearSelected={clearMemberSelected}
                    disableDropdown={assignedMemberOptions.length === 0}
                  />
                  {assignedMemberOptions.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      {(() => {
                        const availableCount = assignedMemberOptions.filter(m => m.isAvailable).length;
                        const totalCount = assignedMemberOptions.length;
                        return (
                          <span className="" style={{ lineHeight: "23px" }}>
                            <span className="text-green-600 dark:text-green-400 font-semibold"><span className="member-available">{availableCount} available </span></span>
                            {' • '}
                            <span className="text-red-600 dark:text-red-400 font-semibold light-red-blg-dv">{totalCount - availableCount} not available</span>
                            {' of '}{totalCount} total members
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {selectedMembers?.length > 0 && (
                <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                  <div className="inner-reportmulselect-blg items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                    {selectedMembers.map((it: any) => (
                      <div
                        key={it.key}
                        className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                      >
                        <div className="flex flex-row items-center justify-start gap-2">
                          {it.isAvailable !== undefined && (
                            <div className={`w-2 h-2 rounded-full ${it.isAvailable ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          )}
                          <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                            {it.name}
                          </div>
                          {it.availableHours !== undefined && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              ({it.availableHours.toFixed(1)}h available)
                            </div>
                          )}
                        </div>
                        <img
                          className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                          loading="lazy"
                          alt="Remove"
                          src={closeDowpDowniconimg}
                          onClick={() => handleRemoveMember(it.key)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Goal Skills Section */}
          <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
            <div className="items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg addtsk_goal-dv">
              <InputsDropdownMultipleSearchSelect
                label="Goal Skills*"
                placeHolder="Select Skills"
                options={skillOptions}
                onSelect={handleSelectSkills}
                inputType="checkbox"
                checkedItems={selectedSkills}
              />

              {selectedSkills?.length > 0 && (
                <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                  <div className="inner-reportmulselect-blg items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                    {selectedSkills.map((it: any) => (
                      <div
                        key={it.key}
                        className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                      >
                        <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                          {it.name}
                        </div>
                        <img
                          className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                          loading="lazy"
                          alt="Remove"
                          src={closeDowpDowniconimg}
                          onClick={() => handleRemoveSkill(it.key)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Phase Tasks Section */}
        <div className="self-stretch rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start gap-4 p-8 mq575:p-3">
          <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
            CareBravo Transition Phases
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            All phases and checklists are pre-configured. Fill in task details for each phase.
          </p>

          <div className="w-full flex flex-col gap-4">
            {phaseTasks.map((phaseTask, index) => {
              const phase = CAREBRAVO_PHASES[index];
              const isExpanded = expandedPhases[phase.id];

              return (
                <div key={phase.id} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  {/* Phase Header */}
                  <div 
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => togglePhaseAccordion(phase.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold dark:text-white">{phase.name}</span>
                      <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
                        {phase.checklists.length} checklists
                      </span>
                    </div>
                    <img
                      className="w-4 h-4 cursor-pointer"
                      src={isExpanded ? arrowIconaccordion : downarrowIconaccordion}
                      alt="toggle"
                    />
                  </div>

                  {/* Phase Content */}
                  {isExpanded && (
                    <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-300 dark:border-gray-600">
                      <div className="grid grid-cols-1 gap-4">
                        {/* Task Name */}
                        <InputsNew
                          label="Task Name*"
                          contentPlaceholder="Enter task name"
                          type="text"
                          value={phaseTask.taskName}
                          onChange={(e) => updatePhaseTask(phase.id, 'taskName', e.target.value)}
                        />

                        {/* Assignee and Skill */}
                        <div className="grid grid-cols-2 gap-4 mq575:grid-cols-1">
                          <InputsDropdownMultipleSearchSelect
                            label="Assignee*"
                            initialValue={phaseTask.assignedMember}
                            placeHolder={selectedMembers.length === 0 ? "Please select goal members first" : "Select Assignee"}
                            options={selectedMembers}
                            onSelect={(key: any, name: any) => handleSelectMember(phase.id, key, name)}
                            inputType="radio"
                            checkedItems={phaseTask.assignedMemberID}
                            disableDropdown={selectedMembers.length === 0}
                          />

                          <InputsDropdownMultipleSearchSelect
                            label="Skill*"
                            initialValue={phaseTask.taskSkill}
                            placeHolder={selectedSkills.length === 0 ? "Please select goal skills first" : "Select Skill"}
                            options={selectedSkills}
                            onSelect={(key: any, name: any) => handleSelectTaskSkill(phase.id, key, name)}
                            inputType="radio"
                            checkedItems={phaseTask.taskSkillID}
                            disableDropdown={selectedSkills.length === 0}
                          />
                        </div>

                        {/* Start Date */}
                        <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                          <DateSelector
                            label="Start Date*"
                            selectedDate={phaseTask.startTaskDate}
                            onChange={(date: string | null) => {
                              const goalStartDate = watch('startGoalDate');
                              const goalEndDate = watch('endGoalDate');

                              if (date && goalStartDate && goalEndDate) {
                                const taskStart = new Date(date);
                                const goalStart = new Date(goalStartDate);
                                const goalEnd = new Date(goalEndDate);

                                if (taskStart < goalStart || taskStart > goalEnd) {
                                  showError('Task start date must be within goal date range');
                                  return;
                                }
                              }

                              updatePhaseTask(phase.id, 'startTaskDate', date);
                            }}
                            nothideParsedValue={true}
                            minDate={watchStartGoalDate ? new Date(watchStartGoalDate) : undefined}
                            maxDate={watchEndGoalDate ? new Date(watchEndGoalDate) : undefined}
                          />
                        </div>

                        {/* Duration */}
                        <div className="grid grid-cols-3 gap-4 mq575:grid-cols-1">
                          <InputsNew
                            label="Days*"
                            contentPlaceholder="1"
                            type="text"
                            value={phaseTask.durationDays}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              updatePhaseTask(phase.id, 'durationDays', value);
                            }}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                          />

                          <InputsNew
                            label="Hours*"
                            contentPlaceholder="0"
                            type="text"
                            value={phaseTask.durationTimeHour}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              updatePhaseTask(phase.id, 'durationTimeHour', value);
                            }}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                          />

                          <InputsNew
                            label="Minutes*"
                            contentPlaceholder="0"
                            type="text"
                            value={phaseTask.durationTimeMinute}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              if (value <= 59) {
                                updatePhaseTask(phase.id, 'durationTimeMinute', value);
                              }
                            }}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                          />
                        </div>

                        {/* End Date - Auto-calculated and Disabled */}
                        <InputsNew
                          label="End Date (Auto-calculated)"
                          contentPlaceholder="End date will be calculated"
                          type="text"
                          value={phaseTask.endDate || ''}
                          disabled={true}
                        />

                        {/* Checklists - Pre-selected and Disabled */}
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-semibold dark:text-white">
                            Checklists (Pre-configured)
                          </label>
                          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-300 dark:border-gray-600 max-h-60 overflow-y-auto">
                            {phase.checklists.map((checklist, idx) => (
                              <div key={idx} className="flex items-start gap-2 py-1">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  disabled
                                  className="mt-1 cursor-not-allowed"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {checklist.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <div className="self-stretch flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="py-2.5 px-[42px] text-gray-700 dark:text-white text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createGoalLoading}
            className="py-2.5 px-[42px] bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createGoalLoading ? 'Creating...' : 'Create Goal'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CarebravoTransitionTemplate;
